// /team — 平台維運團隊(S9):維運者帳號清單 + 角色授權,與 /users 的終端使用者
// 資料維運完全分離。清單 = role ∈ MAINTAINER_ROLES 的帳號(02 §3.3.1 role= 集合查
// 詢 + 前端同集合過濾 belt-and-braces);授權變更沿用 grant-role 契約(platform-owner
// only、reason + case_id 必填、伺服端稽核)。/roles 舊路由 redirect 至此。
//
// 視覺改版(2026-07-10):套用「Graphite Operations Deck」設計系統 —— section-head 分區、
// CopyableId 截斷 ID、grant 主操作 card--primary、revoke 高風險走 card--danger + adm-risk-group。
// IA / route / API / 權限守門(尤其 revoke 禁自撤/禁 platform-owner)一律不動,只改呈現與中文化。

import { useCallback, useEffect, useState } from 'react';
import { canGrantRoles, useAuth } from '../../auth/AuthContext';
import { CopyableId } from '../../components/CopyableId';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { getUserDetail, listUsers, lookupUserByEmail } from '../users/usersApi';
import type { AdminUser } from '../users/usersApi';
import { MAINTAINER_ROLES, roleClass, roleLabel, userStatusLabel, userStatusTone } from '../users/userModel';
import { createTeamMember, deactivateOperator, editOperatorProfile, grantRole, reactivateOperator, revokeRole, sendPasswordReset } from './teamApi';
import { teamActionErrorMessage } from './teamActionError';
import '../_shared/shared.css';
import '../users/users.css';
import './team.css';

const ROLES = ['user', 'developer', 'helpdesk-agent', 'helpdesk-lead', 'cs-readonly', 'admin', 'security-officer', 'platform-owner'];
// Roles a brand-new maintainer account can be created with (restful creatableTeamRole;
// platform-owner is appointed separately, user/developer self-register).
const CREATABLE_ROLES = ['helpdesk-agent', 'helpdesk-lead', 'cs-readonly', 'admin', 'security-officer'];
const MAX_PAGES = 10; // maintainer set is small; bound the cursor walk loudly

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}${e.requestId ? `（${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

export default function TeamPage() {
  // userId 來自 JWT sub claim——silent refresh 後 `user` 物件為 null,claims 不會
  // (review finding:原用 user?.user_id 導致 F5 後自撤防呆失效)。
  const { role: actorRole, userId: actorId } = useAuth();
  const canSubmit = canGrantRoles(actorRole);

  // ---- maintainer roster ----
  const [team, setTeam] = useState<AdminUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<unknown>(null);
  // cursor 未耗盡就到頁數上限 → 清單可能不完整,必須明示(靜默截斷=漏 maintainer)
  const [truncated, setTruncated] = useState(false);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    setTruncated(false);
    try {
      const rows: AdminUser[] = [];
      let cursor: string | undefined;
      let exhausted = false;
      for (let i = 0; i < MAX_PAGES; i++) {
        const page = await listUsers({ roles: MAINTAINER_ROLES, limit: 200, cursor });
        rows.push(...page.users);
        if (!page.nextCursor) {
          exhausted = true;
          break;
        }
        cursor = page.nextCursor;
      }
      setTruncated(!exhausted);
      // belt-and-braces vs a backend build that predates role= (it ignores it)
      setTeam(rows.filter((u) => MAINTAINER_ROLES.includes(u.role ?? '')));
    } catch (e) {
      setLoadErr(e instanceof ApiError ? e : e instanceof Error ? e : new Error('載入失敗'));
      setTeam(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTeam();
  }, [loadTeam]);

  // ---- grant workflow (target from a roster row or from the add-by-id lookup) ----
  const [target, setTarget] = useState<AdminUser | null>(null);
  const [nextRole, setNextRole] = useState('cs-readonly');
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  // 回饋就近顯示在觸發的卡片內(grant / revoke),移出成功後卡片關閉 → 落到名冊區塊。
  // scope 決定回饋渲染位置,取代舊的頁尾固定 + scrollIntoView。
  const [feedbackScope, setFeedbackScope] = useState<'grant' | 'revoke' | 'reset' | 'edit' | 'lifecycle' | 'roster' | null>(null);
  const feedbackNode =
    notice ? (
      <p className="inline-notice inline-notice--good" role="status">
        {notice}
      </p>
    ) : err ? (
      <p className="inline-notice inline-notice--warn" role="alert">
        {err}
      </p>
    ) : null;

  // add-by-id lookup (promote an existing account into the team) — inline「＋」展開於名冊區塊內
  const [lookupId, setLookupId] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  // add mode: 授權既有帳號 (existing) | 建立新的維運帳號 (create)
  const [addMode, setAddMode] = useState<'existing' | 'create'>('existing');
  const [cEmail, setCEmail] = useState('');
  const [cName, setCName] = useState('');
  const [cRole, setCRole] = useState('helpdesk-agent');
  const [cPassMode, setCPassMode] = useState<'invite' | 'password'>('invite');
  const [cPassword, setCPassword] = useState('');
  const [cReason, setCReason] = useState('');
  const [cCase, setCCase] = useState('');
  // 後端密碼規則(restful passwords):至少 6 碼,需含字母、數字、符號各一。前端先擋並給
  // 可讀訊息——否則違規密碼會踩到後端把此 validation 回成 500 INTERNAL_ERROR(訊息不可讀)。
  const cPassOk =
    cPassMode === 'invite' ||
    (cPassword.length >= 6 && /[A-Za-z]/.test(cPassword) && /\d/.test(cPassword) && /[^A-Za-z0-9]/.test(cPassword));
  const cReady = Boolean(cEmail.trim() && cRole && cReason.trim() && cCase.trim() && cPassOk);

  async function submitCreate() {
    setBusy(true);
    setErr('');
    setNotice('');
    setFeedbackScope('roster');
    try {
      const res = await createTeamMember({
        email: cEmail.trim(),
        display_name: cName.trim(),
        role: cRole,
        initial_password: cPassMode === 'password' ? cPassword : undefined,
        reason: cReason.trim(),
        case_id: cCase.trim(),
      });
      setNotice(
        res.invited
          ? `已建立 ${res.member.email}(${roleLabel(res.member.role)})並寄出設定密碼邀請信。`
          : `已建立 ${res.member.email}(${roleLabel(res.member.role)}),可用初始密碼登入。`
      );
      setShowAdd(false);
      setCEmail('');
      setCName('');
      setCPassword('');
      setCReason('');
      setCCase('');
      void loadTeam();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // revoke(移出團隊)— 獨立確認卡;reason + case_id 必填(後端同樣強制)
  const [revokeTarget, setRevokeTarget] = useState<AdminUser | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeCase, setRevokeCase] = useState('');

  async function submitRevoke() {
    if (!revokeTarget) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await revokeRole(revokeTarget.user_id, revokeReason.trim(), revokeCase.trim());
      // 移出成功 → 卡片關閉,回饋落到名冊區塊
      setFeedbackScope('roster');
      setNotice(`已將 ${res.user_id} 自維運團隊移出（${roleLabel(res.previous_role)} → ${roleLabel(res.role)}）。`);
      setRevokeTarget(null);
      setRevokeReason('');
      setRevokeCase('');
      setTarget(null);
      void loadTeam();
    } catch (e) {
      // 失敗 → 卡片仍在,回饋就近顯示於移出卡內
      setFeedbackScope('revoke');
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // reset(寄送密碼重設信)— 輕量確認卡;reason/case_id 選填(後端不強制,僅入稽核)。
  // 動作本身非破壞:只發一次性連結、不動現有密碼,收件人自行設定。
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetReason, setResetReason] = useState('');
  const [resetCase, setResetCase] = useState('');

  async function submitReset() {
    if (!resetTarget) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await sendPasswordReset(resetTarget.user_id, resetReason.trim(), resetCase.trim());
      setFeedbackScope('roster');
      const mins = Math.max(1, Math.round(res.expires_in / 60));
      setNotice(
        `已寄出密碼重設信給 ${resetTarget.email || resetTarget.user_id}(${mins} 分鐘內有效)。收件人點信中連結自行設定新密碼。`
      );
      setResetTarget(null);
      setResetReason('');
      setResetCase('');
      void loadTeam();
    } catch (e) {
      // 失敗 → 卡片仍在,回饋就近顯示(SES 未通/adminURL 未設/帳號非啟用等會如實回報)
      setFeedbackScope('reset');
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // edit(編輯 display_name)— platform-owner only;reason + case_id 必填(後端強制)。
  // 只改 display_name;名稱規則沿用後端 /me 的 1–32 rune。
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editCase, setEditCase] = useState('');
  const editNameLen = [...editName.trim()].length; // rune count, matches backend utf8
  const editNameOk = editNameLen >= 1 && editNameLen <= 32;
  const editReady = Boolean(editNameOk && editReason.trim() && editCase.trim());

  function openEdit(u: AdminUser) {
    setEditTarget(u);
    setEditName(u.display_name || '');
    setEditReason('');
    setEditCase('');
    setNotice('');
    setErr('');
    setFeedbackScope(null);
  }

  async function submitEdit() {
    if (!editTarget) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await editOperatorProfile(editTarget.user_id, editName.trim(), editReason.trim(), editCase.trim());
      setFeedbackScope('roster');
      setNotice(`已更新 ${editTarget.email || editTarget.user_id} 的顯示名稱為「${res.display_name}」。`);
      setEditTarget(null);
      setEditName('');
      setEditReason('');
      setEditCase('');
      void loadTeam();
    } catch (e) {
      setFeedbackScope('edit');
      setErr(teamActionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // deactivate / reactivate(operator 停用/復用)— platform-owner only;reason + case_id 必填。
  // 有別於客訴 suspend/ban;後端擋自我停用與 platform-owner 目標(409)。
  const [lifeTarget, setLifeTarget] = useState<AdminUser | null>(null);
  const [lifeMode, setLifeMode] = useState<'deactivate' | 'reactivate'>('deactivate');
  const [lifeReason, setLifeReason] = useState('');
  const [lifeCase, setLifeCase] = useState('');

  function openLifecycle(u: AdminUser, mode: 'deactivate' | 'reactivate') {
    setLifeTarget(u);
    setLifeMode(mode);
    setLifeReason('');
    setLifeCase('');
    setNotice('');
    setErr('');
    setFeedbackScope(null);
  }

  async function submitLifecycle() {
    if (!lifeTarget) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      if (lifeMode === 'deactivate') {
        await deactivateOperator(lifeTarget.user_id, lifeReason.trim(), lifeCase.trim());
        setNotice(`已停用 ${lifeTarget.email || lifeTarget.user_id}；該帳號已無法登入,現有工作階段已撤銷。`);
      } else {
        await reactivateOperator(lifeTarget.user_id, lifeReason.trim(), lifeCase.trim());
        setNotice(`已復用 ${lifeTarget.email || lifeTarget.user_id}；帳號恢復啟用。`);
      }
      setFeedbackScope('roster');
      setLifeTarget(null);
      setLifeReason('');
      setLifeCase('');
      void loadTeam();
    } catch (e) {
      setFeedbackScope('lifecycle');
      setErr(teamActionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function pickTarget(u: AdminUser) {
    setTarget(u);
    setNextRole(u.role && ROLES.includes(u.role) ? u.role : 'cs-readonly');
    // 換目標必清稽核脈絡,避免上一位的 reason/case_id 張冠李戴進 audit
    setReason('');
    setCaseId('');
    setNotice('');
    setErr('');
    setFeedbackScope(null);
  }

  async function loadById() {
    const q = lookupId.trim();
    if (!q) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      // 直接用 email 或 使用者 ID 都能加:含 '@' 走 email 精準反查,否則當 ID 取詳情。
      let user: AdminUser | null;
      if (q.includes('@')) {
        user = await lookupUserByEmail(q);
        if (!user) throw new Error(`找不到 email 為「${q}」的帳號`);
      } else {
        user = (await getUserDetail(q)).user;
      }
      pickTarget(user);
      setShowAdd(false);
      setLookupId('');
    } catch (e) {
      // 查無帳號 → 回饋就近顯示於名冊區塊(新增列所在)
      setFeedbackScope('roster');
      setErr(errorText(e));
      setTarget(null);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!target) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await grantRole(target.user_id, nextRole, reason.trim(), caseId.trim());
      setTarget({ ...target, role: res.role });
      setFeedbackScope('grant');
      setNotice(`已將 ${res.user_id} 從 ${roleLabel(res.previous_role)} 調整為 ${roleLabel(res.role)}。`);
      setReason('');
      setCaseId('');
      void loadTeam();
    } catch (e) {
      setFeedbackScope('grant');
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="team-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">維運團隊</h1>
          <p className="page-subtitle">
            平台維運者帳號與角色授權（S9），與「使用者」的終端帳號維運分離。變更需平台擁有者,且必填原因與案件編號。
          </p>
        </div>
        <span className={`pill pill-dot ${canSubmit ? 'pill-good' : 'pill-neutral'}`} title="目前登入角色">
          你的角色 · {roleLabel(actorRole ?? undefined)}
        </span>
      </div>

      {/* ---- roster(新增維運者以「＋」於本區塊內 inline 展開,不再獨立專區)---- */}
      <section className="card">
        <div className="section-head">
          <h2 className="section-title">維運者名冊</h2>
          <div className="team-roster-head-right">
            {!loading && loadErr == null && team && (
              <span className="section-meta">共 {team.length} 位維運者</span>
            )}
            {canSubmit && (
              <button
                className={`btn-sm ${showAdd ? 'btn-ghost' : 'btn-primary'}`}
                onClick={() => {
                  setShowAdd((v) => !v);
                  setLookupId('');
                }}
                disabled={busy}
              >
                {showAdd ? '取消' : '＋ 加入維運者'}
              </button>
            )}
          </div>
        </div>
        {showAdd && (
          <div className="team-add-inline">
            <div className="segmented-control" role="tablist" aria-label="加入方式" style={{ marginBottom: 12 }}>
              <button role="tab" aria-selected={addMode === 'existing'} className={addMode === 'existing' ? 'is-active' : ''} onClick={() => { setAddMode('existing'); setErr(''); }}>
                授權既有帳號
              </button>
              <button role="tab" aria-selected={addMode === 'create'} className={addMode === 'create' ? 'is-active' : ''} onClick={() => { setAddMode('create'); setErr(''); }}>
                建立新帳號
              </button>
            </div>

            {addMode === 'existing' ? (
              <>
                <div className="adm-toolbar-form">
                  <label className="adm-field team-field-grow team-field-grow--wide">
                    <span className="adm-field-label text-secondary">Email 或 使用者 ID</span>
                    <input
                      value={lookupId}
                      onChange={(e) => setLookupId(e.target.value)}
                      placeholder="person@example.com 或 u-apac-…"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && lookupId.trim() && !busy) void loadById();
                      }}
                    />
                  </label>
                  <button className="btn-primary" disabled={busy || !lookupId.trim()} onClick={() => void loadById()}>
                    查詢帳號
                  </button>
                </div>
                <p className="adm-hint">授予維運角色給「既有」帳號:輸入 Email 或使用者 ID 即可(免先去「使用者」頁查 ID)。</p>
              </>
            ) : (
              <div className="team-create-form">
                <div className="cf-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">Email(平台網域)</span>
                    <input value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="name@ls-dg.net" autoFocus disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">顯示名稱</span>
                    <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="王小明" disabled={busy} />
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">維運角色</span>
                  <select value={cRole} onChange={(e) => setCRole(e.target.value)} disabled={busy}>
                    {CREATABLE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="segmented-control" aria-label="密碼設定方式">
                  <button className={cPassMode === 'invite' ? 'is-active' : ''} onClick={() => setCPassMode('invite')} disabled={busy}>
                    寄設定密碼邀請信
                  </button>
                  <button className={cPassMode === 'password' ? 'is-active' : ''} onClick={() => setCPassMode('password')} disabled={busy}>
                    直接設初始密碼
                  </button>
                </div>
                {cPassMode === 'password' && (
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">初始密碼</span>
                    <input type="password" value={cPassword} onChange={(e) => setCPassword(e.target.value)} placeholder="至少 6 碼,需含字母、數字、符號" disabled={busy} />
                    {cPassword && !cPassOk && (
                      <span className="adm-hint" style={{ color: 'var(--color-semantic-danger)' }} role="alert">
                        密碼需至少 6 碼,且含字母、數字、符號各一(例如 Test1234!)。
                      </span>
                    )}
                  </label>
                )}
                <div className="cf-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">原因</span>
                    <input value={cReason} onChange={(e) => setCReason(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={cCase} onChange={(e) => setCCase(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <button className="btn-primary" disabled={busy || !cReady} onClick={() => void submitCreate()}>
                  建立維運帳號
                </button>
                <p className="adm-hint">
                  維運帳號不走 App 註冊,Email 必須是平台網域(<span className="adm-mono">@ls-dg.net</span>)。「邀請」會寄設定密碼連結給對方自行設定;「初始密碼」則由你設定、對方直接可登入(登入後建議自行更改)。
                </p>
              </div>
            )}
          </div>
        )}
        {feedbackScope === 'roster' && feedbackNode}
        {truncated && (
          <p className="inline-notice inline-notice--warn" role="alert">
            ⚠️ 名冊掃描達上限仍未讀完(users 資料量大)——以下清單可能不完整,缺漏的維運者請用 email 反查確認。
          </p>
        )}
        {loading && <LoadingState label="載入維運者清單…" />}
        {!loading && loadErr != null && <ErrorState error={loadErr} onRetry={() => void loadTeam()} />}
        {!loading && loadErr == null && team && team.length === 0 && (
          <EmptyState
            icon="👥"
            title="尚無維運者帳號"
            detail="點右上「＋ 加入維運者」把既有帳號授予維運角色。"
          />
        )}
        {!loading && loadErr == null && team && team.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>電子郵件</th>
                  <th>顯示名稱</th>
                  <th>使用者 ID</th>
                  <th>角色</th>
                  <th>狀態</th>
                  <th>建立時間</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {team.map((u) => (
                  <tr key={u.user_id}>
                    <td className="adm-cell-strong">{u.email || '—'}</td>
                    <td>{u.display_name || '—'}</td>
                    <td>
                      <CopyableId value={u.user_id} className="text-tertiary" />
                    </td>
                    <td>
                      <span className={`pill u-role-pill ${roleClass(u.role) || 'pill-neutral'}`}>{roleLabel(u.role)}</span>
                    </td>
                    <td>
                      <span className={`pill pill-dot pill-${userStatusTone(u.status)}`}>{userStatusLabel(u.status)}</span>
                    </td>
                    <td className="adm-mono">{formatTimestampUs(u.created_at, { second: undefined })}</td>
                    <td className="team-roster-actions">
                      {canSubmit ? (
                      <>
                      <button className="btn-ghost btn-sm" disabled={busy} onClick={() => pickTarget(u)}>
                        變更角色
                      </button>
                        <button className="btn-ghost btn-sm" disabled={busy} onClick={() => openEdit(u)} title="編輯顯示名稱">
                          編輯
                        </button>
                        <button
                          className="btn-ghost btn-sm"
                          disabled={busy || u.status !== 'active'}
                          title={
                            u.status !== 'active'
                              ? '僅啟用中的帳號可寄送重設信'
                              : '寄一次性重設連結到此帳號信箱,由本人自行設定新密碼(平台不接觸密碼值)'
                          }
                          onClick={() => {
                            setResetTarget(u);
                            setResetReason('');
                            setResetCase('');
                            setNotice('');
                            setErr('');
                            setFeedbackScope(null);
                          }}
                        >
                          寄送重設信
                        </button>
                        <button
                          className="btn-ghost btn-sm"
                          disabled={busy || u.user_id === actorId || u.role === 'platform-owner'}
                          title={
                            u.role === 'platform-owner'
                              ? 'platform-owner 不可停用（卸任走線下流程）'
                              : u.user_id === actorId
                                ? '不能停用自己（後端亦拒絕）'
                                : u.status === 'deactivated'
                                  ? '恢復此帳號為啟用'
                                  : '停用此維運帳號:立即無法登入並撤銷工作階段（可日後復用）'
                          }
                          onClick={() => openLifecycle(u, u.status === 'deactivated' ? 'reactivate' : 'deactivate')}
                        >
                          {u.status === 'deactivated' ? '復用' : '停用'}
                        </button>
                        <button
                          className="btn-ghost-danger btn-sm"
                          disabled={busy || u.user_id === actorId || u.role === 'platform-owner'}
                          title={
                            u.role === 'platform-owner'
                              ? 'platform-owner 不可經 API 移出（卸任走雙人核准/DB,防互撤歸零）'
                              : u.user_id === actorId
                                ? '不能移出自己（後端亦拒絕自撤）'
                                : '將維運角色歸還為一般使用者'
                          }
                          onClick={() => {
                            setRevokeTarget(u);
                            setRevokeReason('');
                            setRevokeCase('');
                            setNotice('');
                            setErr('');
                            setFeedbackScope(null);
                          }}
                        >
                          移出團隊
                        </button>
                      </>
                      ) : (
                        <span className="text-tertiary">唯讀</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>


      {/* ---- grant editor(主操作區,card--primary 左側 teal 色條)---- */}
      {target && (
        <section className="card card--primary">
          <div className="section-head">
            <h2 className="section-title">授權變更</h2>
            <span className="section-meta">{target.email || target.user_id}</span>
          </div>
          <div className="adm-action-section">
            <dl className="adm-kv">
              <dt>電子郵件</dt>
              <dd>{target.email || '—'}</dd>
              <dt>目前角色</dt>
              <dd>
                <span className={`pill u-role-pill ${roleClass(target.role) || 'pill-neutral'}`}>{roleLabel(target.role)}</span>
              </dd>
              <dt>狀態</dt>
              <dd>{userStatusLabel(target.status)}</dd>
            </dl>
            <div className="adm-toolbar-form">
              <label className="adm-field">
                <span className="adm-field-label text-secondary">新角色</span>
                <select value={nextRole} onChange={(e) => setNextRole(e.target.value)} disabled={!canSubmit || busy}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adm-field team-field-grow team-field-grow--wide">
                <span className="adm-field-label text-secondary">原因</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!canSubmit || busy} />
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">案件編號</span>
                <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={!canSubmit || busy} />
              </label>
              <button
                className="btn-primary"
                disabled={!canSubmit || busy || !reason.trim() || !caseId.trim() || !target || nextRole === target.role}
                onClick={() => void submit()}
              >
                套用角色
              </button>
            </div>
            {!canSubmit && <p className="adm-hint">目前登入角色不能調整角色;後端也會拒絕非平台擁有者。</p>}
            {feedbackScope === 'grant' && feedbackNode}
          </div>
        </section>
      )}

      {/* ---- reset confirm(寄送重設信,非破壞;reason/case_id 選填)---- */}
      {resetTarget && (
        <section className="card">
          <div className="section-head">
            <h2 className="section-title">寄送密碼重設信</h2>
            <span className="section-meta">{resetTarget.email || resetTarget.user_id}</span>
          </div>
          <div className="adm-action-section">
            <p className="inline-notice">
              將寄一封<strong>一次性重設連結</strong>到 <strong>{resetTarget.email || resetTarget.user_id}</strong> 的信箱(連結指向本主控台
              <code> /reset-password</code>,15 分鐘內有效)。<strong>不會變更現有密碼</strong>;由收件人點連結自行設定,平台與操作者全程不接觸密碼值。此操作留稽核痕。
            </p>
            <div className="adm-toolbar-form">
              <label className="adm-field team-field-grow team-field-grow--wide">
                <span className="adm-field-label text-secondary">原因（選填）</span>
                <input value={resetReason} onChange={(e) => setResetReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">案件編號（選填）</span>
                <input value={resetCase} onChange={(e) => setResetCase(e.target.value)} disabled={busy} />
              </label>
              <button className="btn-primary" disabled={busy} onClick={() => void submitReset()}>
                {busy ? '寄送中…' : '確認寄送'}
              </button>
              <button
                className="btn-ghost"
                disabled={busy}
                onClick={() => {
                  setResetTarget(null);
                  setResetReason('');
                  setResetCase('');
                }}
              >
                取消
              </button>
            </div>
            {feedbackScope === 'reset' && feedbackNode}
          </div>
        </section>
      )}

      {/* ---- edit operator profile(編輯 display_name;platform-owner only)---- */}
      {editTarget && (
        <section className="card">
          <div className="section-head">
            <h2 className="section-title">編輯維運帳號</h2>
            <span className="section-meta">{editTarget.email || editTarget.user_id}</span>
          </div>
          <div className="adm-action-section">
            <p className="inline-notice">
              僅可編輯<strong>顯示名稱</strong>（1–32 字）;角色變更走「變更角色」,Email／手機走各自流程。此操作留稽核痕。
            </p>
            <div className="adm-toolbar-form">
              <label className="adm-field team-field-grow team-field-grow--wide">
                <span className="adm-field-label text-secondary">顯示名稱</span>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} disabled={busy} placeholder="王小明" />
                {editName.trim() && !editNameOk && (
                  <span className="adm-hint" style={{ color: 'var(--color-semantic-danger)' }} role="alert">
                    顯示名稱需為 1–32 個字。
                  </span>
                )}
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">原因（必填）</span>
                <input value={editReason} onChange={(e) => setEditReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">案件編號（必填）</span>
                <input value={editCase} onChange={(e) => setEditCase(e.target.value)} disabled={busy} />
              </label>
              <button className="btn-primary" disabled={busy || !editReady} onClick={() => void submitEdit()}>
                {busy ? '儲存中…' : '儲存'}
              </button>
              <button className="btn-ghost" disabled={busy} onClick={() => setEditTarget(null)}>
                取消
              </button>
            </div>
            {feedbackScope === 'edit' && feedbackNode}
          </div>
        </section>
      )}

      {/* ---- deactivate / reactivate(operator 停用/復用;platform-owner only)---- */}
      {lifeTarget && (
        <section className={`card ${lifeMode === 'deactivate' ? 'card--danger' : ''}`}>
          <div className="section-head">
            <h2 className="section-title">{lifeMode === 'deactivate' ? '停用維運帳號' : '復用維運帳號'}</h2>
            {lifeMode === 'deactivate' ? (
              <span className="pill pill-danger pill-dot">高風險 · 停止存取</span>
            ) : (
              <span className="section-meta">{lifeTarget.email || lifeTarget.user_id}</span>
            )}
          </div>
          <div className="adm-action-section">
            <p className={`inline-notice ${lifeMode === 'deactivate' ? 'inline-notice--warn' : ''}`}>
              {lifeMode === 'deactivate' ? (
                <>
                  將<strong>停用</strong> <strong>{lifeTarget.email || lifeTarget.user_id}</strong>（{roleLabel(lifeTarget.role)}）:帳號立即
                  <strong> 無法登入</strong>、現有工作階段<strong>全部撤銷</strong>,但保留帳號與角色（可日後「復用」）。有別於客訴停權／封禁。此操作留稽核痕。
                </>
              ) : (
                <>
                  將<strong>復用</strong> <strong>{lifeTarget.email || lifeTarget.user_id}</strong>,帳號恢復啟用、可再次登入。此操作留稽核痕。
                </>
              )}
            </p>
            <div className="adm-toolbar-form">
              <label className="adm-field team-field-grow team-field-grow--wide">
                <span className="adm-field-label text-secondary">原因（必填）</span>
                <input value={lifeReason} onChange={(e) => setLifeReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">案件編號（必填）</span>
                <input value={lifeCase} onChange={(e) => setLifeCase(e.target.value)} disabled={busy} />
              </label>
            </div>
            <div className="adm-risk-group">
              <button
                className={lifeMode === 'deactivate' ? 'btn-danger' : 'btn-primary'}
                disabled={busy || !lifeReason.trim() || !lifeCase.trim()}
                onClick={() => void submitLifecycle()}
              >
                {busy ? '處理中…' : lifeMode === 'deactivate' ? '確認停用' : '確認復用'}
              </button>
              <button className="btn-ghost" disabled={busy} onClick={() => setLifeTarget(null)}>
                取消
              </button>
            </div>
            {feedbackScope === 'lifecycle' && feedbackNode}
          </div>
        </section>
      )}

      {/* ---- revoke confirm(高風險、不可逆,card--danger + adm-risk-group 圈起)---- */}
      {revokeTarget && (
        <section className="card card--danger">
          <div className="section-head">
            <h2 className="section-title">移出維運團隊</h2>
            <span className="pill pill-danger pill-dot">高風險 · 存取撤銷</span>
          </div>
          <div className="adm-action-section">
            <p className="inline-notice inline-notice--warn">
              將 <strong>{revokeTarget.email || revokeTarget.user_id}</strong>（{roleLabel(revokeTarget.role)}）的維運角色歸還為
              <strong> 一般使用者</strong>。此帳號將立即失去管理主控台存取權;此操作全程稽核留痕。
            </p>
            <div className="adm-toolbar-form">
              <label className="adm-field team-field-grow team-field-grow--wide">
                <span className="adm-field-label text-secondary">原因（必填）</span>
                <input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field team-field-grow">
                <span className="adm-field-label text-secondary">案件編號（必填）</span>
                <input value={revokeCase} onChange={(e) => setRevokeCase(e.target.value)} disabled={busy} />
              </label>
            </div>
            <div className="adm-risk-group">
              <button
                className="btn-danger"
                disabled={busy || !revokeReason.trim() || !revokeCase.trim()}
                onClick={() => void submitRevoke()}
              >
                確認移出
              </button>
              <button className="btn-ghost" disabled={busy} onClick={() => setRevokeTarget(null)}>
                取消
              </button>
            </div>
            {feedbackScope === 'revoke' && feedbackNode}
          </div>
        </section>
      )}
    </div>
  );
}
